/*
 * ************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *   Copyright 2018 Adobe Systems Incorporated
 *   All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe Systems Incorporated and its suppliers,
 * if any.  The intellectual and technical concepts contained
 * herein are proprietary to Adobe Systems Incorporated and its
 * suppliers and are protected by all applicable intellectual property
 * laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe Systems Incorporated.
 * ************************************************************************
 */

import classNames from 'classnames';
import React from 'react';

import Heading from '@react/react-spectrum/Heading';
import { Step, StepList } from '@react/react-spectrum/StepList';
import { defineMessages, injectIntl } from '@nebula/izzy/intl';

import ModalDialog from '@nebula/luna/dist/core/modal-dialog';
import GLOBAL_MSG from '@nebula/luna/dist/global.strings';

import SessionCreateConnectContent from './connect.content';
import SessionCreateFormContent from './form.content';
import SessionCreateIntroContent from './intro.content';
import SessionCreateQuickConnectContent from './quick.connect.content';
import styles from './styles/index.styl';

const MSG = defineMessages({
  title: {
    id: 'gemini.session.create.title',
    defaultMessage: 'Create New Session'
  }
});
const SessionCreate = ({
  createForm,
  connecting = true,
  image,
  izzy,
  link,
  nameField,
  onConnectedDevice,
  onErrorDevice,
  onQuickConnect,
  onStepIndexChange,
  quickConnectConfig = {},
  pin,
  quickConnect,
  saving,
  showQuickConnectOption = false,
  stepIndex = 0,
  urlField,
  ...props
}) => (
  <ModalDialog
    mode="alert"
    className={classNames(styles.root, { 'hide-buttons': saving })}
    confirmLabel={stepIndex === 0 ? izzy(GLOBAL_MSG.start)
      : stepIndex === 2 ? izzy(GLOBAL_MSG.done) : !quickConnect
        ? izzy(GLOBAL_MSG.next) : izzy(GLOBAL_MSG.back)}
    cancelLabel={stepIndex !== 2 ? izzy(GLOBAL_MSG.cancel) : null}
    onCancel={createForm.onReset}
    onConfirm={(type, payload) => (type === 'primary'
      ? stepIndex === 0
        ? onStepIndexChange(stepIndex + 1)
        : stepIndex === 1 ? createForm.onSubmit(payload) : createForm.onReset()
      : onStepIndexChange(stepIndex - 1))
    }
    secondaryLabel={stepIndex !== 0 && connecting && !quickConnect ? izzy(GLOBAL_MSG.back) : null}
    variant={stepIndex === 0 || !quickConnect ? 'confirmation' : 'information'}
    {...props}
  >
    <React.Fragment>
      <div className={classNames(
        'spectrum-Dialog-header',
        'spectrum-Dialog-header--confirmation'
      )}
      >
        <Heading size={3} className="spectrum-Dialog-title">{izzy(MSG.title)}</Heading>
        <StepList className={styles.steps} interaction="off" selectedIndex={stepIndex}>
          <Step />
          <Step />
          <Step />
        </StepList>
      </div>
      <div
        className="spectrum-Dialog-content"
        data-omega-feature="create session"
      >
        {stepIndex === 0 ? (
          <SessionCreateIntroContent
            image={image}
            izzy={izzy}
            onQuickConnect={onQuickConnect}
            quickConnect={quickConnect}
            showQuickConnectOption={showQuickConnectOption}
          />
        ) : quickConnect ? (
          <SessionCreateQuickConnectContent
            connecting={connecting}
            image={image}
            izzy={izzy}
            onConnected={onConnectedDevice}
            onError={onErrorDevice}
            stepIndex={stepIndex}
            {...quickConnectConfig}
          />
        ) : stepIndex === 1 ? (
          <SessionCreateFormContent
            izzy={izzy}
            nameField={nameField}
            saving={saving}
            sessionForm={createForm}
            urlField={urlField}
          />
        ) : stepIndex === 2 && (
          <SessionCreateConnectContent
            connecting={connecting}
            image={image}
            izzy={izzy}
            link={link}
            pin={pin}
          />
        )}
      </div>
    </React.Fragment>
  </ModalDialog>
);

SessionCreate.displayName = 'SessionCreate';

export default injectIntl(SessionCreate);
